import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  fetchProjectsData,
  getCachedProjectsData,
} from "../../utils/projectsData";
import {
  PAGE_FADE_UP,
  PAGE_REVEAL_DELAY,
  PAGE_REVEAL_EASE,
} from "../../utils/pageReveal";

const LANG_COLORS = {
  JavaScript: "#f7df1e",
  TypeScript: "#3178c6",
  HTML: "#e34c26",
  CSS: "#563d7c",
  Python: "#3572A5",
  Java: "#b07219",
  Swift: "#F05138",
  Kotlin: "#A97BFF",
  default: "#8e8e8e",
};

const GRID_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: PAGE_REVEAL_DELAY,
      staggerChildren: 0.08,
    },
  },
};

const CARD_VARIANTS = {
  hidden: PAGE_FADE_UP.hidden,
  visible: {
    ...PAGE_FADE_UP.visible,
    transition: {
      duration: 0.5,
      ease: PAGE_REVEAL_EASE,
    },
  },
};

function ProjectSkeletonCard() {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border-2 border-lightDeep/70 p-5 sm:p-6 animate-pulse">
      <div className="flex items-center gap-2">
        <div className="h-7 w-36 rounded-full bg-lightDeep/20" />
        <div className="h-5 w-14 rounded-full bg-lightDeep/15" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-full rounded-full bg-lightDeep/15" />
        <div className="h-4 w-[92%] rounded-full bg-lightDeep/15" />
        <div className="h-4 w-[70%] rounded-full bg-lightDeep/15" />
      </div>
      <div className="mt-auto flex items-center justify-between pt-3">
        <div className="flex items-center gap-4">
          <div className="h-4 w-20 rounded-full bg-lightDeep/15" />
          <div className="h-4 w-10 rounded-full bg-lightDeep/15" />
        </div>
        <div className="h-4 w-14 rounded-full bg-lightDeep/15" />
      </div>
    </div>
  );
}

export default function ProjectsSection() {
  const cachedProjects = getCachedProjectsData();
  const [repos, setRepos] = useState(cachedProjects?.repos ?? []);
  const [loading, setLoading] = useState(!cachedProjects);
  const [error, setError] = useState(cachedProjects?.error ?? null);

  useEffect(() => {
    let active = true;

    fetchProjectsData().then((result) => {
      if (!active) return;
      setRepos(result.repos);
      setError(result.error);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="projects"
      className="text-beige min-h-screen px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28
        pt-16 pb-20 sm:pt-20 sm:pb-24"
    >
      {/* Sticky title */}
      <div className="sticky top-10 z-20 bg-deep py-3 -mx-4 sm:-mx-8 md:-mx-12 lg:-mx-20 xl:-mx-28 px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28 mb-10 sm:mb-12 md:mb-16">
        <h2
          className="font-extrabold text-beige
          text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
        >
          PROJECTS.
        </h2>
      </div>

      {loading && (
        <motion.div
          variants={PAGE_FADE_UP}
          initial="hidden"
          animate="visible"
          transition={{
            delay: PAGE_REVEAL_DELAY,
            duration: 0.4,
            ease: PAGE_REVEAL_EASE,
          }}
          className="grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <ProjectSkeletonCard key={index} />
          ))}
        </motion.div>
      )}

      {error && (
        <p className="mt-6 text-base text-lightBrown">
          Could not load projects: {error}
        </p>
      )}

      {!loading && (
        <motion.div
          variants={GRID_VARIANTS}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {repos.map((repo) => {
            const langColor = LANG_COLORS[repo.language] || LANG_COLORS.default;
            const cardClassName =
              "group flex h-full flex-col gap-3 rounded-2xl border-2 border-lightDeep p-5 sm:p-6 shadow-sm transition-[transform,background-color,border-color,box-shadow] duration-200 ease-out active:scale-[0.985] md:hover:-translate-y-0.5 md:hover:border-lightBrown md:hover:bg-lightBrown/10 md:hover:shadow-lg";

            const cardContent = (
              <>
                {/* Name + private badge */}
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-beige text-lg sm:text-xl md:text-2xl transition-colors duration-200 md:group-hover:text-pink">
                    {repo.name}
                  </h3>
                  {repo.isPrivate && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-lightDeep2 text-lightDeep2 transition-colors duration-200 md:group-hover:border-lightBrown md:group-hover:text-lightBrown">
                      private
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-lightDeep1 text-sm sm:text-base leading-relaxed flex-1 transition-colors duration-200 md:group-hover:text-beige">
                  {repo.description || "No description provided."}
                </p>

                {/* Footer: language + stars + link hint */}
                <div className="flex items-center justify-between mt-auto pt-2">
                  <div className="flex items-center gap-4 text-xs sm:text-sm text-lightDeep2 transition-colors duration-200 md:group-hover:text-lightBrown">
                    {repo.language && (
                      <span className="flex items-center gap-1.5">
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: langColor }}
                        />
                        {repo.language}
                      </span>
                    )}
                    {repo.stars > 0 && (
                      <span className="flex items-center gap-1">
                        ★ {repo.stars}
                      </span>
                    )}
                  </div>
                  {repo.url && (
                    <span className="text-xs text-beige font-semibold transition-colors duration-200 md:group-hover:text-pink md:group-hover:underline">
                      View →
                    </span>
                  )}
                </div>
              </>
            );

            return repo.url ? (
              <motion.a
                key={repo.id}
                variants={CARD_VARIANTS}
                whileTap={{ scale: 0.985 }}
                href={repo.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClassName}
              >
                {cardContent}
              </motion.a>
            ) : (
              <motion.div
                key={repo.id}
                variants={CARD_VARIANTS}
                className={cardClassName}
              >
                {cardContent}
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </section>
  );
}
